import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import { aboutIntro, platformStats, contactInfo, faqItems } from '@/mocks/aboutData';

const CONTACT_FORM_URL = 'http://my783e.cn/';

const aboutBreadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'http://my783e.cn/' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://my783e.cn/about' },
  ],
};

const aboutOrgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '吃瓜网官网',
  alternateName: '51吃瓜网官网 · 91吃瓜网官网 · 黑料吃瓜网官网',
  url: 'http://my783e.cn/',
  description: '吃瓜网官网是国内领先的娱乐八卦资讯聚合平台，每日更新网红塌房、明星黑料、博主反差等最新热门爆料内容。',
  email: contactInfo.email,
  address: {
    '@type': 'PostalAddress',
    addressLocality: '北京',
    addressRegion: '朝阳区',
    streetAddress: '网络文化产业园A座',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: contactInfo.phone,
    contactType: 'customer service',
    availableLanguage: 'Chinese',
  },
};

const aboutFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快与您联系。');
      return;
    }

    setFormStatus('loading');

    try {
      const formData = new FormData(form);
      if (honeypotField) {
        formData.delete('company_alt');
      }

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！我们的商务团队会在2个工作日内与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后重试' : serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <PageMeta
        title="关于我们 — 吃瓜网官网 | 品牌介绍 · 商务合作"
        description="吃瓜网官网是国内领先的娱乐八卦资讯平台，每日更新网红塌房、明星黑料、博主反差等热门内容。了解吃瓜网品牌故事、平台数据、商务合作与联系方式。"
        keywords="吃瓜网官网,关于吃瓜网,品牌介绍,商务合作,51吃瓜网官网,91吃瓜网官网,黑料吃瓜网官网,吃瓜网站,联系我们"
        canonicalUrl="http://my783e.cn/about"
        schemaData={[aboutOrgSchema, aboutBreadcrumbSchema, aboutFaqSchema]}
      />

      <Navbar />

      <main>
        {/* About Intro Section */}
        <section className="relative w-full bg-foreground-950 overflow-hidden">
          <div className="max-w-6xl mx-auto px-4 md:px-6 pt-24 md:pt-28 pb-12 md:pb-16">
            <div className="flex flex-col lg:flex-row items-start gap-8 md:gap-12">
              <div className="w-full lg:w-[38%]">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold border border-foreground-500 text-foreground-400 mb-4">
                  关于吃瓜网
                </span>
                <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src={aboutIntro.image}
                    alt="吃瓜网官网品牌介绍"
                    title="吃瓜网官网 - 关于我们"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="w-full lg:w-[62%]">
                <h1 className="text-2xl md:text-3xl lg:text-5xl font-heading font-bold text-background-50 leading-tight mb-4">
                  {aboutIntro.title}<br />
                  <span className="text-primary-400">{aboutIntro.subtitle}</span>
                </h1>
                <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-4">
                  {aboutIntro.description}
                </p>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {aboutIntro.mission}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-background-50 px-4 md:px-6 py-4 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Platform Stats */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                平台数据
              </h2>
              <p className="text-sm text-foreground-600">用数据说话，吃瓜网的实力有目共睹</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {platformStats.map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-6 md:p-8 text-center">
                  <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                    <i className={`${stat.icon} text-2xl`}></i>
                  </span>
                  <p className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-2">
                    {stat.value}
                  </p>
                  <p className="text-lg font-bold text-foreground-800 mb-2">{stat.label}</p>
                  <p className="text-sm text-foreground-600">{stat.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / FAQ */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">关于吃瓜网，你可能想了解这些</p>
            </div>
            <div className="space-y-3">
              {faqItems.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 md:p-6 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-semibold text-foreground-950 pr-4">{faq.question}</span>
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 group-open:rotate-45 transition-transform duration-200 flex-shrink-0">
                      <i className="ri-add-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-sm text-foreground-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  商务合作 · 联系我们
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  如果您有品牌推广、广告投放或内容合作的需求，欢迎通过以下方式与我们联系。我们的商务团队会在第一时间回复您。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">电子邮箱</p>
                      <p className="text-sm text-foreground-600">{contactInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">官方微信</p>
                      <p className="text-sm text-foreground-600">{contactInfo.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-weibo-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">官方微博</p>
                      <p className="text-sm text-foreground-600">{contactInfo.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">公司地址</p>
                      <p className="text-sm text-foreground-600">{contactInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">
                  发送合作咨询
                </h3>
                <p className="text-xs text-foreground-600 mb-6">
                  填写以下表单，我们会尽快回复
                </p>
                <form
                  id="contact-form"
                 
                  onSubmit={handleContactSubmit}
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 text-sm text-foreground-950 border border-background-300 outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 text-sm text-foreground-950 border border-background-300 outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="block text-xs font-medium text-foreground-700 mb-1.5">公司/品牌名称</label>
                    <input
                      id="contact-company"
                      type="text"
                      name="company"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 text-sm text-foreground-950 border border-background-300 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">合作需求</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 text-sm text-foreground-950 border border-background-300 outline-none focus:border-primary-400 transition-colors resize-none"
                      required
                    />
                    <p className="text-xs text-foreground-500 mt-1">限500字以内</p>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-all duration-200 disabled:opacity-60"
                  >
                    {formStatus === 'loading' ? '提交中...' : '提交咨询'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 font-medium text-center">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-primary-500 font-medium text-center">{formMessage}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}